import { UnifiedSelect } from '../../components/UnifiedSelect';
import { SceneSummary } from '../../components/FeedbackScene';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  MessageSquare,
  ArrowUpRight,
  RefreshCw,
  Search,
  CheckCircle2,
  Inbox,
  Clock3,
  RotateCcw,
} from 'lucide-react';
import { useLoad, AdminHeading, LoadError } from './shared';
import { api, json, date } from '../../api';
import { Modal } from '../../components/Modal';
import './support.css';
import { supportCategoryLabels } from '../../../shared/support';
import { SupportConversation } from '../../components/SupportConversation';
import '../contact.css';
type Ticket = {
  scene?: any;
  id: string;
  body: string;
  source: string;
  category: string;
  contact: string;
  email?: string;
  username: string;
  user_id: string | null;
  internal: number;
  game_name: string;
  version: string;
  launch_id: string | null;
  ua: string;
  status: string;
  note: string;
  created_at: string;
};
type Inbox = {
  items: Ticket[];
  total: number;
  summary: { total: number; open: number; processing: number; resolved: number };
};
const labels: Record<string, string> = {
  open: '待处理',
  processing: '处理中',
  resolved: '已解决 / 已关闭',
  ...supportCategoryLabels,
};
export function SupportSummary() {
  const { value, error, reload } = useLoad<Inbox>('/admin/support?status=open');
  useEffect(() => {
    const timer = setInterval(() => {
      if (!document.hidden) void reload();
    }, 60000);
    return () => clearInterval(timer);
  }, []);
  return (
    <section className="support-summary admin-panel" aria-label="用户反馈提醒">
      <div className="support-summary-head">
        <span className="support-summary-icon">
          <MessageSquare size={25} />
        </span>
        <div>
          <h2>
            用户反馈 <b>{value?.summary.open ?? '—'}</b>
          </h2>
          <p>
            {error
              ? '暂时无法读取反馈'
              : value?.summary.open
                ? '有新的问题等待处理，先听听用户怎么说。'
                : '暂时没有待处理反馈。'}
            {value &&
              value.summary.processing > 0 &&
              ` 另有 ${value.summary.processing} 条处理中。`}
          </p>
        </div>
        <Link className="button" to="/admin/support">
          查看反馈
          <ArrowUpRight size={16} />
        </Link>
      </div>
      {error && <button onClick={() => void reload()}>重新加载</button>}
      {value?.items.slice(0, 3).map((t) => (
        <Link className="support-summary-row" to={'/admin/support?id=' + t.id} key={t.id}>
          <span>
            <b>{t.game_name || labels[t.category]}</b>
            <small>
              {t.username}
              {t.internal ? ' · 内部测试' : ''}
            </small>
          </span>
          <p>{t.body}</p>
          <time>{date(t.created_at)}</time>
        </Link>
      ))}
    </section>
  );
}
export function AdminSupport() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState('all'),
    [audience, setAudience] = useState('all'),
    [search, setSearch] = useState(''),
    [page, setPage] = useState(1);
  const { value, error, loading, reload } = useLoad<Inbox>(
    '/admin/support?' + new URLSearchParams({ status, audience, q: search, page: String(page) }),
  );
  const [selected, setSelected] = useState<Ticket | null>(null),
    [note, setNote] = useState(''),
    [saving, setSaving] = useState(false),
    [saveError, setSaveError] = useState(''),
    [savedMessage, setSavedMessage] = useState('');
  function open(t: Ticket) {
    setSelected(t);
    setNote(t.note);
    setSaveError('');
    setSavedMessage('');
  }
  async function saveResult(status: string) {
    if (!selected || saving) return;
    setSaving(true);
    setSaveError('');
    setSavedMessage('');
    try {
      await api('/admin/support/' + selected.id, json('PATCH', { status, note }));
      setSelected((current) => (current ? { ...current, status, note } : current));
      setSavedMessage(
        status === 'resolved'
          ? '问题已关闭，记录已保留'
          : status === 'open'
            ? '问题已重新打开'
            : '处理结果已保存',
      );
      await reload();
    } catch (e) {
      setSaveError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }
  const requestedId = params.get('id');
  useEffect(() => {
    if (!requestedId) return;
    let active = true;
    void api<Ticket>('/admin/support/' + encodeURIComponent(requestedId))
      .then((t) => {
        if (active) open(t);
      })
      .catch((e) => {
        if (active) setSaveError((e as Error).message);
      });
    return () => {
      active = false;
    };
  }, [requestedId]);
  const close = () => {
    setSelected(null);
    setParams({}, { replace: true });
  };
  return (
    <>
      <AdminHeading kicker="MOYU ISLAND / SUPPORT" title="用户反馈">
        <button className="button secondary" disabled={loading} onClick={() => void reload()}>
          <RefreshCw size={16} />
          刷新
        </button>
      </AdminHeading>
      <LoadError error={error || (!selected ? saveError : '')} />
      <div className="support-metrics">
        {(['all', 'open', 'processing', 'resolved'] as const).map((k) => (
          <button
            key={k}
            onClick={() => {
              setStatus(k);
              setPage(1);
            }}
            className={status === k ? 'active' : ''}
          >
            <span>
              {k === 'all' ? (
                <Inbox size={20} />
              ) : k === 'resolved' ? (
                <CheckCircle2 size={20} />
              ) : (
                <Clock3 size={20} />
              )}
              {k === 'all' ? '全部反馈' : labels[k]}
            </span>
            <strong>{value?.summary[k === 'all' ? 'total' : k] ?? '—'}</strong>
          </button>
        ))}
      </div>
      <section className="admin-panel support-inbox">
        <div className="support-list-heading">
          <h2>{status === 'all' ? '全部反馈' : labels[status]}</h2>
          <span>{value?.total ?? '—'} 条反馈</span>
        </div>
        <div className="support-tools">
          <UnifiedSelect
            aria-label="反馈用户范围"
            value={audience}
            onChange={(e) => {
              setAudience(e.target.value);
              setPage(1);
            }}
          >
            <option value="all">全部用户</option>
            <option value="external">真实用户</option>
            <option value="internal">内部测试</option>
          </UnifiedSelect>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setSearch(String(new FormData(e.currentTarget).get('q') || ''));
              setPage(1);
            }}
          >
            <Search size={17} />
            <input name="q" aria-label="搜索反馈" placeholder="搜索内容、游戏或用户" />
            <button>搜索</button>
          </form>
        </div>
        <div className="support-ticket-list">
          {value?.items.map((t) => (
            <button className="support-ticket" key={t.id} onClick={() => setParams({ id: t.id })}>
              <span className="support-ticket-top">
                <b>{t.game_name || labels[t.category]}</b>
                <i className={'support-state ' + t.status}>{labels[t.status]}</i>
                <time>{date(t.created_at)}</time>
              </span>
              <p>{t.body}</p>
              <span className="support-ticket-meta">
                {t.username} · {labels[t.category]}
                {t.internal ? ' · 内部测试' : ''}
                <span>查看与处理 →</span>
              </span>
            </button>
          ))}
        </div>
        {!loading && !error && !value?.items.length && (
          <div className="table-empty">暂无符合条件的反馈</div>
        )}
        <div className="support-pagination">
          <span>共 {value?.total ?? 0} 条</span>
          <button disabled={page === 1 || loading} onClick={() => setPage(page - 1)}>
            上一页
          </button>
          <span>{page}</span>
          <button
            disabled={!value || page * 20 >= value.total || loading}
            onClick={() => setPage(page + 1)}
          >
            下一页
          </button>
        </div>
      </section>
      {selected && (
        <Modal
          title="反馈详情"
          onClose={close}
          wide
          className="support-workspace-dialog"
          dismissible={!saving}
        >
          <div className="support-detail">
            <div className="support-resolution-bar">
              <div>
                <span className={'support-state ' + selected.status}>
                  {labels[selected.status]}
                </span>
                <small>反馈 #{selected.id.slice(0, 8).toUpperCase()}</small>
              </div>
              <div className="support-resolution-actions">
                {selected.status === 'resolved' ? (
                  <button
                    className="button secondary"
                    disabled={saving}
                    onClick={() => void saveResult('open')}
                  >
                    <RotateCcw size={16} />
                    重新打开
                  </button>
                ) : (
                  <>
                    {selected.status === 'open' && (
                      <button
                        className="button secondary"
                        disabled={saving}
                        onClick={() => void saveResult('processing')}
                      >
                        开始处理
                      </button>
                    )}
                    <button
                      className="button"
                      disabled={saving}
                      onClick={() => void saveResult('resolved')}
                    >
                      <CheckCircle2 size={16} />
                      {saving ? '保存中…' : '解决并关闭'}
                    </button>
                  </>
                )}
              </div>
              <p>关闭仅结束当前处理，不删除记录；用户再次补充后会重新进入待处理。</p>
              {savedMessage && (
                <p role="status" className="support-save-success">
                  {savedMessage}
                </p>
              )}
              {saveError && (
                <p role="alert" className="support-save-error">
                  {saveError}
                </p>
              )}
            </div>
            <div className="support-ticket-top">
              <b>{selected.game_name || labels[selected.category]}</b>
            </div>
            {selected.user_id&&<Link className="button secondary small" to={"/admin/achievements?user="+encodeURIComponent(selected.user_id)}>查看玩家成就</Link>}
            {!selected.user_id && <p className="support-body">{selected.body}</p>}
            <dl>
              <dt>提交用户</dt>
              <dd>
                {selected.username}
                {selected.internal ? ' · 内部测试' : ''}
              </dd>
              <dt>提交时间</dt>
              <dd>{date(selected.created_at)}</dd>
              <dt>注册邮箱 / 历史联系方式</dt>
              <dd>{selected.email || selected.contact || '未填写'}</dd>
              {selected.version && (
                <>
                  <dt>游戏版本</dt>
                  <dd>{selected.version}</dd>
                </>
              )}
            </dl>
            {selected.user_id && (
              <SupportConversation
                statusOverride={selected.status}
                id={selected.id}
                admin
                onUpdate={() => {
                  void reload();
                  setSelected((current) =>
                    current ? { ...current, status: 'processing' } : current,
                  );
                }}
              />
            )}
            {selected.launch_id && (
              <details className="support-evidence">
                <summary>问题现场与诊断记录</summary>{' '}
                {selected.launch_id && (
                  <SceneSummary scene={selected.scene} id={selected.id} admin />
                )}
                {selected.launch_id && (
                  <p>
                    <Link
                      to={
                        '/admin/diagnostics?' +
                        new URLSearchParams({ q: selected.launch_id, audience: 'all' })
                      }
                    >
                      查看关联诊断记录 →
                    </Link>
                    <small className="support-retention">
                      诊断日志保留 14 天；反馈内容与处理记录会保留。
                    </small>
                  </p>
                )}
              </details>
            )}
            <label>
              内部处理备注
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={2000}
                rows={4}
                placeholder="记录排查进展或处理结果，不会发送给用户"
              />
            </label>
            <button
              className="button secondary"
              disabled={saving}
              onClick={() => void saveResult(selected.status)}
            >
              {saving ? '保存中…' : '保存内部备注'}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
